import type { CascadeColumn, CascadeStack } from '@/cascade/types';
import type { InlineCapability } from '@/registries/sidebars';

/**
 * The props a cascade column hands its panel: its own id under the registered
 * `idProp`, plus each declared ancestor's id under its named prop.
 *
 * Ancestor binding walks the true parent CHAIN (via `.parent`), not positional left
 * neighbours: under fan-out (ADR-0027) a column's left neighbour can be a sibling
 * subtree rather than an ancestor, so a positional scan would bind an unrelated
 * column's id. The chain is ordered nearest-first, so `find` picks the nearest
 * matching ancestor (`patientFile(A) → appointmentDetails → patientFile(B)` binds
 * patient B for a child of patientFile(B)). For a linear stack the chain is exactly
 * the left-hand neighbours, so degenerate workspaces are unchanged.
 *
 * A pure function rather than a computed inside `CascadeColumn`, because the draft
 * store needs the same answer without a component: an identified draft (ADR-0029 §G)
 * is keyed by these props, and its zone instance is created from them — the store
 * cannot ask the column, which by then is already gone.
 */
export function bindColumnProps(
  stack: CascadeStack,
  column: CascadeColumn,
  inline: InlineCapability | undefined,
): Record<string, unknown> {
  if (!inline) return {};
  const bag: Record<string, unknown> = { [inline.idProp]: column.id };
  const ancestors: CascadeColumn[] = [];
  for (let p = column.parent; p !== undefined; ) {
    const candidate = stack[p];
    if (!candidate) break;
    ancestors.push(candidate);
    p = candidate.parent;
  }
  for (const ancestor of inline.parentIds ?? []) {
    const match = ancestors.find((c) => c.type === ancestor.type);
    if (match) bag[ancestor.idProp] = match.id;
  }
  return bag;
}
